import React, { Suspense } from 'react';
import { useLocation } from 'react-router';
import { useLocalStorage } from 'react-use';

import {
  AutoModalPriority,
  useAutoModal,
} from '../../common/AutoModalProvider';
import useGetCompanyVerificationInfo from '../../common/hooks/useGetCompanyVerificationInfo';
import useShouldShowWhatsappVerification from '../../common/hooks/useShouldShowWhatsappVerification';
import CompanyVerificationModalVariantController from '../../modules/CompanyDashboard/shared/CompanyHeader/components/CompanyVerificationModalVariantController';
import ErrorBoundaryWithCrashReporting from '../Error/ErrorBoundaryWithCrashReporting';
import { useGlintsVIPMembershipInfo } from '../GlintsVIPMembershipInfo/hooks/useGlintsVIPMembershipInfo';
import { useCompanyVerificationModalsContext } from './CompanyVerificationModalsProvider';
const CompanyVerificationRejectedModal = React.lazy(
  () => import('./CompanyVerificationRejectedModal')
);
const CompanyVerificationSuccessModal = React.lazy(
  () => import('./CompanyVerificationSuccessModal')
);

type VerificationStatusModalProps = {
  enabled?: boolean;
};

export function RenderCompanyVerificationStatusModal({
  enabled = true,
}: VerificationStatusModalProps) {
  const {
    verificationInfo,
    modals: {
      canShowCompanyVerificationRejectedModal,
      canShowCompanyVerificationSuccessModal,
    },
  } = useGetCompanyVerificationInfo();
  const { isRejectionModalClosed, isSuccessModalClosed } =
    useCompanyVerificationModalsContext();
  const {
    vipMembershipInfo: { hasNoInventoryRecord },
  } = useGlintsVIPMembershipInfo();
  const lastVerificationRequestId =
    verificationInfo?.lastVerificationRequest?.id;
  const [isRejectionModalSeen] = useLocalStorage(
    `company-rejection-modal-seen:${lastVerificationRequestId}`,
    false
  );
  const [isSuccessModalSeen] = useLocalStorage(
    `company-success-modal-seen:${lastVerificationRequestId}`,
    false
  );
  const isWAVerificationFlowVisible = useShouldShowWhatsappVerification();

  const shouldShowRejectedModal = Boolean(
    canShowCompanyVerificationRejectedModal &&
      !isRejectionModalClosed &&
      !isRejectionModalSeen
  );
  const shouldShowSuccessModal = Boolean(
    canShowCompanyVerificationSuccessModal &&
      hasNoInventoryRecord !== false &&
      !isSuccessModalClosed &&
      !isSuccessModalSeen
  );
  const canShowRejected = useAutoModal(
    AutoModalPriority.Verification,
    enabled && !isWAVerificationFlowVisible && shouldShowRejectedModal
  );
  const canShowSuccess = useAutoModal(
    AutoModalPriority.Status,
    enabled && !isWAVerificationFlowVisible && shouldShowSuccessModal
  );

  if (!enabled) return null;

  if (isWAVerificationFlowVisible) {
    return null;
  }

  return (
    <ErrorBoundaryWithCrashReporting fallback={null}>
      <Suspense fallback={null}>
        <If condition={canShowRejected}>
          <CompanyVerificationRejectedModal />
        </If>
        <If condition={canShowSuccess}>
          <CompanyVerificationSuccessModal />
        </If>
      </Suspense>
    </ErrorBoundaryWithCrashReporting>
  );
}

export default function RenderCompanyVerificationModals() {
  const { pathname } = useLocation();

  if (pathname === '/dashboard') {
    return <CompanyVerificationModalVariantController />;
  }

  return (
    <ErrorBoundaryWithCrashReporting fallback={null}>
      <Suspense fallback={null}>
        <CompanyVerificationModalVariantController />
        <RenderCompanyVerificationStatusModal />
      </Suspense>
    </ErrorBoundaryWithCrashReporting>
  );
}
